import { Skeleton } from "@/components/ui/skeleton";
import InfoTooltip from "@/components/ui/InfoTooltip";
import { useDashboard } from "@/contexts/DashboardContext";
import { CALL_VOLUME_COLORS } from "@/constants/colors";
import { WidgetTitle } from "@/components/widgets/WidgetTitle";
import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

interface CallVolumeWidgetData {
  time: Date;
  inboundCall: number;
  outboundCall: number;
}

type GroupByType = "year" | "month" | "day" | "hour" | "daydiffyear";

const getGroupByFromDateRange = (dateRange?: { from: Date, to: Date }): GroupByType => {
  if (!dateRange) return "day";
  const thisYear = new Date().getFullYear();
  const isDateRangeTheSameYear = dateRange.from.getFullYear() === thisYear && dateRange.to.getFullYear() === thisYear;
  const isDateRangeTheSameMonth = dateRange.from.getMonth() === dateRange.to.getMonth() && isDateRangeTheSameYear;
  const isDateRangeTheSameDay = dateRange.from.getDate() === dateRange.to.getDate() && isDateRangeTheSameMonth && isDateRangeTheSameYear;

  if (isDateRangeTheSameDay) {
    return "hour";
  } else if (isDateRangeTheSameYear) {
    return "day";
  } else {
    return "daydiffyear";
  }
};

// Build placeholder buckets (0 calls) spanning dateRange so axes still render when there's no data
const generateEmptyBuckets = (dateRange: { from: Date, to: Date }, groupBy: GroupByType): CallVolumeWidgetData[] => {
  const buckets: CallVolumeWidgetData[] = [];
  const cursor = new Date(dateRange.from);

  if (groupBy === 'hour') {
    cursor.setMinutes(0, 0, 0);
    for (let i = 0; i < 24; i++) {
      buckets.push({ time: new Date(cursor), inboundCall: 0, outboundCall: 0 });
      cursor.setHours(cursor.getHours() + 1);
    }
    return buckets;
  }

  cursor.setHours(0, 0, 0, 0);
  const end = new Date(dateRange.to);
  end.setHours(0, 0, 0, 0);
  while (cursor.getTime() <= end.getTime()) {
    buckets.push({ time: new Date(cursor), inboundCall: 0, outboundCall: 0 });
    cursor.setDate(cursor.getDate() + 1);
  }
  return buckets;
};

const formatTooltipTime = (time: Date, groupBy: GroupByType) => {
  if (groupBy === "hour") {
    return time.toLocaleString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } else if (groupBy === "month") {
    return time.toLocaleString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  }
  return time.toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CustomTooltip = ({ active, payload, label, theme, groupBy }: any) => {
  // Rendered via <Tooltip content={<CustomTooltip theme={...} groupBy={...} />} /> —
  // recharts clones this element and injects active/payload/label at render time.
  if (active && payload && payload.length) {
    const currentInboundColor = theme === 'dark' ? CALL_VOLUME_COLORS.INBOUND.dark : CALL_VOLUME_COLORS.INBOUND.light;
    const currentOutboundColor = theme === 'dark' ? CALL_VOLUME_COLORS.OUTBOUND.dark : CALL_VOLUME_COLORS.OUTBOUND.light;
    return (
      <div className={`border rounded-lg shadow-md p-3 min-w-[220px] ${theme === 'dark' ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`} style={{ fontSize: 14 }}>
        <p className="font-semibold dark:text-white">{formatTooltipTime(label, groupBy)}</p>
        <p style={{ color: currentInboundColor }}>Inbound: {payload[0]?.value}</p>
        <p style={{ color: currentOutboundColor }}>Outbound: {payload[1]?.value}</p>
        <p style={{ color: CALL_VOLUME_COLORS.INBOUND_DIRECT.light }}>Inbound Direct: {payload[2]?.value}</p>
        <p style={{ color: CALL_VOLUME_COLORS.INTERNAL.light }}>Internal: {payload[3]?.value}</p>
      </div>
    );
  }
  return null;
};

interface CallVolumeChartProps {
  processedData: CallVolumeWidgetData[];
  theme: string;
  groupBy: GroupByType;
  tickGap: number;
  isAllDataZero: boolean;
  currentInboundColor: string;
  currentOutboundColor: string;
}

const formatXAxisTick = (value: Date, groupBy: GroupByType) => {
  if (groupBy === "hour") {
    return value.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } else if (groupBy === "daydiffyear") {
    return value.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
  }
  return value.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' });
};

const legendFormatter = (value: string, currentInboundColor: string, currentOutboundColor: string) => {
  if (value === 'Inbound') return <span style={{ color: currentInboundColor }}>{value}</span>;
  if (value === 'Outbound') return <span style={{ color: currentOutboundColor }}>{value}</span>;
  if (value === 'Inbound Direct') return <span style={{ color: CALL_VOLUME_COLORS.INBOUND_DIRECT.light }}>{value}</span>;
  if (value === 'Internal') return <span style={{ color: CALL_VOLUME_COLORS.INTERNAL.light }}>{value}</span>;
  return value;
};

const CallVolumeChart: React.FC<CallVolumeChartProps> = ({
  processedData,
  theme,
  groupBy,
  tickGap,
  isAllDataZero,
  currentInboundColor,
  currentOutboundColor,
}) => {
  const ticks = processedData.length > tickGap
    ? Array.from({ length: Math.ceil(processedData.length / tickGap) }, (_, i) => processedData[i * tickGap]?.time).filter(Boolean)
    : processedData.map(d => d.time);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={processedData} margin={{ left: 0, right: 25, top: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="time"
          tick={{ fontSize: 14, fill: theme === 'dark' ? '#fff' : '#6b7280' }}
          tickFormatter={(value) => formatXAxisTick(value, groupBy)}
          interval={0}
          ticks={ticks}
        />
        <YAxis
          tick={{ fontSize: 14, fill: theme === 'dark' ? '#fff' : '#6b7280' }}
          domain={isAllDataZero ? [0, 10] : undefined}
          ticks={isAllDataZero ? [0, 2, 4, 6, 8, 10] : undefined}
          allowDecimals={false}
        />
        <Tooltip content={<CustomTooltip theme={theme} groupBy={groupBy} />} cursor={{ fill: theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)" }} />
        <Legend
          verticalAlign="top"
          align="center"
          iconType="rect"
          wrapperStyle={{ fontSize: 14, paddingBottom: 10 }}
          formatter={(value) => legendFormatter(value, currentInboundColor, currentOutboundColor)}
        />
        <Bar dataKey="inboundCall" name="Inbound" fill={currentInboundColor} radius={[4, 4, 0, 0]} />
        <Bar dataKey="outboundCall" name="Outbound" fill={currentOutboundColor} radius={[4, 4, 0, 0]} />
        <Bar dataKey="inboundDirectCall" name="Inbound Direct" fill={CALL_VOLUME_COLORS.INBOUND_DIRECT.light} radius={[4, 4, 0, 0]} />
        <Bar dataKey="internalCall" name="Internal" fill={CALL_VOLUME_COLORS.INTERNAL.light} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
};

const CallVolumeGroupWidget: React.FC<{ data: CallVolumeWidgetData[]; dateRange: { from: Date, to: Date }, icon?: React.ReactNode; loading?: boolean; error?: string }> = ({ data, dateRange, loading }) => {
  const { theme } = useDashboard();

  // Convert data to local timezone and group by month if needed
  const processedData = data?.map(item => ({
    ...item,
    time: new Date(item.time)
  }));

  // Update dateRange based on data if available
  if (processedData && processedData.length > 0) {
    const times = processedData.map(item => item.time.getTime());
    const minTime = new Date(Math.min(...times));
    const maxTime = new Date(Math.max(...times));
    dateRange = {
      from: minTime,
      to: maxTime
    };
  }

  const groupBy: GroupByType = getGroupByFromDateRange(dateRange);

  // No data at all: show empty axes (still 0..10 scale) instead of a "no data" message
  const chartData = processedData?.length ? processedData : generateEmptyBuckets(dateRange, groupBy);

  // Check if all data points are 0
  const isAllDataZero = chartData?.every(item =>
    item.inboundCall === 0 && item.outboundCall === 0
  ) ?? false;

  // Get current colors based on theme
  const currentInboundColor = theme === 'dark' ? CALL_VOLUME_COLORS.INBOUND.dark : CALL_VOLUME_COLORS.INBOUND.light;
  const currentOutboundColor = theme === 'dark' ? CALL_VOLUME_COLORS.OUTBOUND.dark : CALL_VOLUME_COLORS.OUTBOUND.light;

  const containerRef = React.useRef<any>(null);

  const [tickGap, setGap] = React.useState<number>(1);
  React.useEffect(() => {
    if (containerRef.current && chartData?.length) {
      const items = chartData.length;
      const width = (containerRef.current.container?.clientWidth || containerRef.current.offsetWidth || 600) - 60 - 25;
      const required = groupBy === 'daydiffyear' ? 80 : 60;
      const calculated = Math.ceil(required / (width / items));
      setGap(calculated);
    }
  }, [groupBy, chartData]);

  return (
    <div className="w-full min-w-[200px] min-h-[260px] rounded-lg border flex flex-col bg-white dark:bg-[#0C3451] dark:text-white dark:border-[#3C5C73] p-3">
      <div ref={containerRef} className="flex items-center gap-2">
        <WidgetTitle className="truncate">Call Volume</WidgetTitle>
        <InfoTooltip
          content="The total number of inbound calls, inbound direct calls, outbound calls and internal calls within the given time."
          iconClassName="text-primary dark:text-white"
        />
      </div>
      <div style={{ height: '100%' }}>
        {renderCallVolumeContent({
          loading,
          processedData: chartData,
          isAllDataZero,
          theme,
          groupBy,
          tickGap,
          currentInboundColor,
          currentOutboundColor,
        })}
      </div>
    </div>
  );
};

interface RenderCallVolumeContentArgs {
  loading?: boolean;
  processedData?: CallVolumeWidgetData[];
  isAllDataZero: boolean;
  theme: string;
  groupBy: GroupByType;
  tickGap: number;
  currentInboundColor: string;
  currentOutboundColor: string;
}

function renderCallVolumeContent({
  loading,
  processedData,
  isAllDataZero,
  theme,
  groupBy,
  tickGap,
  currentInboundColor,
  currentOutboundColor,
}: RenderCallVolumeContentArgs) {
  if (loading) {
    return <Skeleton className="w-full h-full rounded-none" />;
  }

  return (
    <CallVolumeChart
      processedData={processedData ?? []}
      theme={theme}
      groupBy={groupBy}
      tickGap={tickGap}
      isAllDataZero={isAllDataZero}
      currentInboundColor={currentInboundColor}
      currentOutboundColor={currentOutboundColor}
    />
  );
}

export default CallVolumeGroupWidget;
